import { FC, ReactElement } from 'react';
import { Link } from 'react-router-dom';

import 'store/bebyggelseobjektUtkastSlice';

import { RaaExpandablePreview, RaaSpinner } from 'raa-react';

import { Colors } from 'components/map/Styles';
import { hourglassCircleSvg, penCircleSvg } from 'components/map/icons';

import { BebyggelseobjektUtkastIndex, ModelError, User } from 'models';
import { RequestStatus } from 'models/appModels/Request';

import { RegistreringsstatustypEnum } from 'enums';

interface UtkastListProps {
  user: User | undefined;
  list: Array<BebyggelseobjektUtkastIndex> | undefined;
  requestStatus: RequestStatus;
  error: ModelError | undefined;
  action?: (u: BebyggelseobjektUtkastIndex) => ReactElement;
  // ModelError.error_fields indexerade med utkastId.
  // Ex: error_fields = { [f6c7bd40-5887-4897-b789-5669963950d2]: ErrorField[] }
  utkastsWithErrors?: ModelError['error_fields'];
}

const UtkastList: FC<UtkastListProps> = (props: UtkastListProps): ReactElement => {
  const getSortText = (utkast: BebyggelseobjektUtkastIndex): string => {
    if (!utkast.lm_registerbyggnad && !utkast.lm_registerenhetsreferens) return '';
    const registerenhetsreferens = utkast?.lm_registerenhetsreferens
      ?.map((fastighet) => fastighet.beteckning)
      .join(', ');
    const husnummer = utkast.lm_registerbyggnad?.husnummer;
    return `${registerenhetsreferens}, hus ${husnummer}`;
  };

  const resolveUtkastIcon = (status: RegistreringsstatustypEnum): string => {
    return status === RegistreringsstatustypEnum.REDIGERBAR
      ? `data:image/svg+xml,${encodeURIComponent(penCircleSvg(Colors.GREEN))}`
      : `data:image/svg+xml,${encodeURIComponent(hourglassCircleSvg(Colors.GREEN))}`;
  };

  if (props.requestStatus === 'pending') {
    return (
      <div className="tw-flex tw-justify-center tw-py-8">
        <RaaSpinner size="3rem" />
      </div>
    );
  }
  if (props.error) {
    return (
      <p className="tw-py-4 tw-text-raa-error-red">
        {props.error ? props.error.message : 'Något gick fel vid hämtning av utkast. Prova att ladda om sidan.'}
      </p>
    );
  }

  return (
    <>
      {props.list && (
        <RaaExpandablePreview height={120} ariaLabel="Lista med bebyggelseutkast">
          <ul className="tw-mt-2" data-testid="utkastList">
            {Array.from(props.list)
              .sort((a: BebyggelseobjektUtkastIndex, b: BebyggelseobjektUtkastIndex) =>
                getSortText(a) < getSortText(b) ? -1 : 1
              )
              .map((utkast: BebyggelseobjektUtkastIndex) => {
                const utkastStatus = utkast.registrering.registreringsstatustyp_id;
                const utkastErrors = props.utkastsWithErrors && props.utkastsWithErrors[utkast.utkast_id];

                return (
                  <div className="tw-flex tw-items-center tw-justify-between" key={utkast.utkast_id}>
                    <div className="tw-mb-4 tw-flex tw-max-w-[85%] tw-items-center">
                      <img
                        src={resolveUtkastIcon(utkastStatus)}
                        alt={`Ikon för ${
                          utkastStatus === RegistreringsstatustypEnum.REDIGERBAR ? 'redigerbart' : 'inskickat'
                        } utkast`}
                        className="tw-mr-2 tw-h-6"
                      />
                      <li className="tw-flex tw-max-w-full tw-flex-col">
                        <Link
                          className="raa-link tw-w-fit tw-font-semibold"
                          to={
                            utkastStatus === RegistreringsstatustypEnum.REDIGERBAR
                              ? `/utkast/${utkast.utkast_id}/redigera`
                              : `/utkast/${utkast.utkast_id}`
                          }
                        >
                          <span>
                            {utkast.titel !== utkast.bebyggelsenummer && utkast.titel}{' '}
                            <span className="tw-italic">({utkast.bebyggelsenummer})</span>
                          </span>
                        </Link>
                        <span className="tw-mr-4 tw-max-w-full tw-truncate tw-capitalize">
                          {utkast.berorda_fastigheter?.join(', ').toLowerCase()}
                        </span>
                        {utkastErrors?.map(({ field: utkastId, message }) => (
                          <p key={utkastId + '_' + message} className="tw-text-raa-error-red">
                            {message}
                          </p>
                        ))}
                      </li>
                    </div>
                    {props.action && props.action(utkast)}
                  </div>
                );
              })}
          </ul>
        </RaaExpandablePreview>
      )}
    </>
  );
};

export default UtkastList;
